import { useEffect, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, Icon, Modal, Pane, PaneHead, ToggleRow } from '@/components/kit';
import apiClient from '@/services/apiClient';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { ManagerMenu } from '../components/ManagerMenu';

/**
 * Clinic manager · Settings ▸ Message wallet ▸ Patient messages — `g_wmsgs`
 * (f27c). "What sends a message, and what each one costs."
 *
 * THE CLINIC NEVER COMPOSES ONE. Six moments send; each is a switch. Two ship
 * OFF because a reply does the same job free — a bill and a document are handed
 * over at the desk, or asked for.
 *
 * WHAT A SWITCH STOPS IS THE PAID NUDGE, NEVER THE PORTAL ROW. The free inbox
 * entry is the canonical record of the event and is written either way, so a
 * patient whose clinic has the bill switch off still finds their bill; what
 * stops is the clinic paying to interrupt them about it. Which is also why the
 * count beside a switched-OFF moment is not zero and is not a guess: the moment
 * still happens, it is still measured, and the figure means "this is what
 * turning it on would cost you".
 *
 * EVERY NUMBER HERE IS MEASURED, NOT ASSUMED. The per-month count is this
 * clinic's own last 30 days off its portal inbox; the unit rate is the same
 * quote a real debit is priced with. count × rate = the amount, checkable — the
 * board's own line about the ledger.
 *
 * SAVE IS THE COMMIT, as on the alert families: the switches move locally and
 * nothing is written until Save, or the page's Discard would be a lie.
 */

interface Moment {
  key: string;
  label: string;
  detail: string;
  on: boolean;
  perMonth: number;
  costMinor: number;
}

interface Quiet {
  from: string;
  to: string;
  daysOff: number[];
}

type RxMode = 'private' | 'pharmacy_link' | 'full';

/** What each prescription-message mode puts in front of whoever can read it. */
const RX_MODES: { key: RxMode; label: string; caption: string }[] = [
  {
    key: 'private',
    label: 'Private',
    caption: 'Only the patient can open it — a one-time sign-in link.',
  },
  {
    key: 'pharmacy_link',
    label: 'Pharmacy link',
    caption:
      'Any pharmacy can open the prescription from the link — so can anyone the message is forwarded to.',
  },
  {
    key: 'full',
    label: 'Full message',
    caption:
      'The drug, dose and instructions are written in the WhatsApp message itself, plus the pharmacy link.',
  },
];

interface MomentsPayload {
  moments: Moment[];
  quiet: Quiet;
  rxWhatsappMode?: RxMode;
  currency: string;
  unitMinor: number;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** "9 pm", "8 am" — the board writes clock times the way a person says them. */
function spoken(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm ?? '');
  if (!m) return hhmm;
  const h = Number(m[1]);
  const min = Number(m[2]);
  const suffix = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return min === 0 ? `${h12} ${suffix}` : `${h12}:${m[2]} ${suffix}`;
}

/**
 * A minor-unit figure in the clinic's own currency.
 *
 * WHOLE UNITS ONLY ONCE THERE ARE SOME. The board's figures are hundreds of
 * pounds, where the piasters are noise — but a clinic whose month costs 3.40
 * must not read four rows of "EGP 0" under a total of "EGP 1". Under ten, the
 * decimals ARE the number.
 */
function money(minor: number, currency: string): string {
  const major = (minor ?? 0) / 100;
  const digits = major > 0 && major < 10 ? 2 : 0;
  return `${(currency ?? 'EGP').toUpperCase()} ${major.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

/* Stable fallbacks: a fresh `{}` per render is a changing dependency, and the page's
   chrome effect then re-ran until React gave up at the update-depth limit. */
const NO_MOMENTS: Record<string, boolean> = {};
const DEFAULT_QUIET: { from: string; to: string; daysOff: number[] } = { from: '21:00', to: '08:00', daysOff: [5] };

export function ManagerMessagesPage() {
  const go = useNavigate();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<{
    moments: Record<string, boolean>;
    quiet: Quiet;
    rxWhatsappMode: RxMode;
  } | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [editQuiet, setEditQuiet] = useState(false);

  usePageMeta({ title: 'Patient messages' });

  const q = useQuery({
    queryKey: ['cm-message-moments'],
    staleTime: 60_000,
    queryFn: async (): Promise<MomentsPayload> => {
      const res = await apiClient.get('/clinic-settings/message-moments');
      return (res.data?.data ?? res.data) as MomentsPayload;
    },
  });

  useEffect(() => {
    if (q.data && !draft) {
      setDraft({
        moments: Object.fromEntries(q.data.moments.map((m) => [m.key, m.on])),
        quiet: { ...q.data.quiet },
        rxWhatsappMode: q.data.rxWhatsappMode ?? 'private',
      });
    }
  }, [q.data, draft]);

  const save = useMutation({
    mutationFn: async (next: {
      moments: Record<string, boolean>;
      quiet: Quiet;
      rxWhatsappMode: RxMode;
    }) => {
      await apiClient.put('/clinic-settings/message-moments', next);
    },
    onSuccess: () => {
      toast.success('Saved. It applies to the next message, not the ones already sent.');
      void qc.invalidateQueries({ queryKey: ['cm-message-moments'] });
      setDraft(null);
    },
    onError: () => toast.error('That was refused. Nothing has changed.'),
  });

  const data = q.data;
  const currency = data?.currency ?? 'EGP';
  const moments = data?.moments ?? [];
  const state = draft?.moments ?? NO_MOMENTS;
  const quiet = draft?.quiet ?? data?.quiet ?? DEFAULT_QUIET;

  /* THE TWO FIGURES THE BOARD PUTS SIDE BY SIDE: what is on now, and what all
     six would be. The second is what makes the first mean something.
     Summing six numbers every render costs nothing — a memo over values that
     are rebuilt each render would not have memoised anything anyway. */
  const onCount = moments.filter((m) => state[m.key]).length;
  const onCost = moments.reduce((n, m) => n + (state[m.key] ? m.costMinor : 0), 0);
  const allCost = moments.reduce((n, m) => n + m.costMinor, 0);

  const dirty =
    !!draft &&
    !!data &&
    (data.moments.some((m) => state[m.key] !== m.on) ||
      draft.rxWhatsappMode !== (data.rxWhatsappMode ?? 'private') ||
      draft.quiet.from !== data.quiet.from ||
      draft.quiet.to !== data.quiet.to ||
      draft.quiet.daysOff.join() !== data.quiet.daysOff.join());

  const selected = moments.find((m) => m.key === picked) ?? null;

  const crumb = usePhoneCrumb('Settings', 'Patient messages');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!selected,
      onPaneClose: () => setPicked(null),
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      title: 'Patient messages',
      sub: 'what sends a message, and what each one costs',
      search: null,
      primary: {
        label: save.isPending ? 'Saving…' : 'Save',
        disabled: !dirty || save.isPending,
        onClick: () => draft && save.mutate(draft),
      },
      pane: selected ? (
        <MomentPane
          moment={selected}
          on={!!state[selected.key]}
          currency={currency}
          unitMinor={data?.unitMinor ?? 0}
          quiet={quiet}
          onClose={() => setPicked(null)}
          onLedger={() => go('/clinic-manager/settings/wallet?door=history')}
        />
      ) : (
        <Pane
          head={
            <PaneHead
              overline="The selected moment"
              title="Nothing picked"
              sub="pick a moment to see what the patient reads"
            />
          }
        >
          <div className="emptystate">
            <p className="t-sec">
              Every one of these is sent by the moment itself. The clinic never composes one by
              hand.
            </p>
          </div>
        </Pane>
      ),
    },
    [selected, state, dirty, save.isPending, currency, quiet, picked],
  );

  return (
    <div className="maie desk cm-root">
      <div className="filterrow">
        <p className="t-sec mut">
          <span className="w6 brand" style={{ cursor: 'pointer' }}
            onClick={() => go('/clinic-manager/settings')}>‹ Settings</span>{' '}
          <span className="mut">▸</span>{' '}
          <span className="w6 brand" style={{ cursor: 'pointer' }}
            onClick={() => go('/clinic-manager/settings/wallet')}>Message wallet</span>{' '}
          <span className="mut">▸</span> <span className="t-sec sec w6">Patient messages</span>
        </p>
      </div>

      <div className="dcontent scroll">
        {q.isLoading ? (
          <p className="t-sec mut">Reading what sends a message…</p>
        ) : q.isError ? (
          <div className="emptystate">
            <p className="es-t">The switches did not come back</p>
            <p className="t-sec mut">Nothing has been changed.</p>
          </div>
        ) : (
          <>
            <p className="t-ovl mut" style={{ margin: '0 0 8px' }}>Send a message when</p>

            {moments.map((m) => (
              <div key={m.key} onClick={() => setPicked(m.key === picked ? null : m.key)}>
                <ToggleRow
                  className={picked === m.key ? 'cm-moment sel' : 'cm-moment'}
                  label={
                    <span className="flex" style={{ gap: 12, alignItems: 'baseline' }}>
                      <span className="grow">{m.label}</span>
                      {/* The board's right-hand pair: how often, and what that
                          costs. Measured, so it is the same arithmetic the
                          ledger shows — count × rate. */}
                      <span className="t-cap mut" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {/* A MOMENT THAT HAS NEVER HAPPENED HERE COSTS NOTHING
                            TO PREDICT. "EGP 0" reads as free; "not yet" reads
                            as what it is — this clinic has no 30 days of it to
                            measure yet. */}
                        {m.perMonth > 0 ? `~${m.perMonth} / mo` : 'not yet'}
                        <br />
                        <span className="w6 num ink">
                          {m.perMonth > 0 ? money(m.costMinor, currency) : '—'}
                        </span>
                      </span>
                    </span>
                  }
                  captionOn={m.detail}
                  captionOff={m.detail}
                  checked={!!state[m.key]}
                  onChange={(next) =>
                    setDraft((d) => (d ? { ...d, moments: { ...d.moments, [m.key]: next } } : d))
                  }
                />
              </div>
            ))}

            <Card kind="accent" className="mt12">
              <p className="t-sec brand">
                {onCount === 0 ? (
                  'Nothing is on — no patient hears from this clinic automatically.'
                ) : allCost === 0 ? (
                  /* Nothing has happened in 30 days, so there is nothing to
                     price. Saying so beats printing EGP 0 as if these were
                     free. */
                  <>
                    {onCount} on now. Nothing has been sent in the last 30 days, so there is no
                    rate to go on yet — the figures fill in as the clinic uses it.
                  </>
                ) : (
                  <>
                    {onCount} on now: <span className="w6">{money(onCost, currency)}</span> a month
                    · all {moments.length} would be{' '}
                    <span className="w6">{money(allCost, currency)}</span>
                  </>
                )}
              </p>
            </Card>

            <Card>
              <p className="t-ovl mut mb8">Quiet hours</p>
              <p className="t-strong">
                {spoken(quiet.from)} – {spoken(quiet.to)}
                {quiet.daysOff.length
                  ? ` · ${quiet.daysOff.map((d) => DAY_NAMES[d]).join(' & ')} off`
                  : ' · every day'}{' '}
                <span className="brand" style={{ cursor: 'pointer' }} onClick={() => setEditQuiet(true)}>
                  <Icon name="pencil" size="sm" />
                </span>
              </p>
              <p className="t-cap mut mt4">
                not politeness — a clinic that messages at midnight gets reported, and a blocked
                number costs the channel the whole patient portal rests on
              </p>
            </Card>

            <Card>
              <p className="t-ovl mut mb8">Prescription messages</p>
              <div role="radiogroup" aria-label="What a prescription message shows">
                {RX_MODES.map((o) => {
                  const checked = (draft?.rxWhatsappMode ?? 'private') === o.key;
                  return (
                    <label
                      key={o.key}
                      className="flex"
                      style={{ gap: 10, alignItems: 'flex-start', padding: '8px 0', cursor: 'pointer' }}
                    >
                      <input
                        type="radio"
                        name="rx-whatsapp-mode"
                        value={o.key}
                        checked={checked}
                        style={{ marginTop: 4, accentColor: 'var(--color-brand-600, var(--brand-600))' }}
                        onChange={() =>
                          setDraft((d) => (d ? { ...d, rxWhatsappMode: o.key } : d))
                        }
                      />
                      <span className="grow">
                        <span className="t-strong">{o.label}</span>
                        <br />
                        <span className="t-cap mut">{o.caption}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </Card>

            <p className="t-sec mut" style={{ marginTop: 12 }}>
              Two switches ship OFF because a reply does the same job free: a bill and a document
              are handed over at the desk, or asked for.
            </p>
          </>
        )}
      </div>

      <QuietHoursModal
        open={editQuiet}
        quiet={quiet}
        onClose={() => setEditQuiet(false)}
        onSave={(next) => {
          setDraft((d) => (d ? { ...d, quiet: next } : d));
          setEditQuiet(false);
        }}
      />
    </div>
  );
}

/**
 * The selected moment, as the board docks it: what fires it, what the patient
 * reads, the three figures, and the ledger line they add up to.
 */
function MomentPane({
  moment, on, currency, unitMinor, quiet, onClose, onLedger,
}: {
  moment: Moment;
  on: boolean;
  currency: string;
  unitMinor: number;
  quiet: Quiet;
  onClose: () => void;
  onLedger: () => void;
}) {
  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline="The selected moment"
          title={moment.label}
          sub={`~${moment.perMonth} / mo · ${money(moment.costMinor, currency)} a month`}
          wrapTitle
        />
      }
      foot={
        <button type="button" className="btn btn-outline" style={{ flex: 1, minHeight: 44 }}
          onClick={onLedger}>
          See it in the ledger
        </button>
      }
    >
      <Card>
        <p className="t-ovl mut">What fires it</p>
        <p className="t-sec sec mt4">{moment.detail}</p>
        <p className="t-cap mut">
          the clinic never composes it by hand — the moment sends it
        </p>
      </Card>

      <Card>
        <p className="t-ovl mut mb8">What the patient reads</p>
        {/* THE NUDGE CARRIES NO CLINICAL DETAIL AND NO FIGURE — only that the
            thing exists, and a link. A preview on a shared phone is read by
            whoever holds it, so everything else waits behind the portal's own
            "is this you?". */}
        <p className="t-sec sec">
          A line saying what is ready, and a single-use link into the portal. Never the result,
          the drug or the amount — a message preview on a shared phone is read by whoever holds
          it.
        </p>
      </Card>

      <div className="cm-fields" style={{ display: 'flex', gap: 8 }}>
        <Card style={{ flex: 1, minWidth: 0 }}>
          <p className="t-ovl mut">A month</p>
          <p className="t-strong mt4">~{moment.perMonth}</p>
        </Card>
        <Card style={{ flex: 1, minWidth: 0 }}>
          <p className="t-ovl mut">Each</p>
          <p className="t-strong mt4">{money(unitMinor, currency)}</p>
        </Card>
        <Card style={{ flex: 1, minWidth: 0 }}>
          <p className="t-ovl mut">Costs</p>
          <p className="t-strong mt4">{money(moment.costMinor, currency)}</p>
        </Card>
      </div>

      <Card>
        <p className="t-ovl mut">On the ledger</p>
        <p className="t-sec sec mt4">
          {moment.label} · {moment.perMonth} × {money(unitMinor, currency)} ·{' '}
          −{money(moment.costMinor, currency)}
        </p>
        <p className="t-cap mut">count × rate = amount, every month, checkable</p>
      </Card>

      <Card style={{ borderColor: 'var(--warning-600)', background: 'var(--warning-100)' }}>
        <p className="t-sec amb">
          Quiet hours hold: nothing leaves between {spoken(quiet.from)} and {spoken(quiet.to)}
          {quiet.daysOff.length
            ? `, and nothing leaves on ${quiet.daysOff.map((d) => DAY_NAMES[d]).join(' or ')}`
            : ''}
          . A blocked number costs the whole channel.
        </p>
        {!on ? (
          <p className="t-cap mut mt8">
            This one is off, so nothing leaves at any hour. The patient still finds it in the
            portal.
          </p>
        ) : null}
      </Card>
    </Pane>
  );
}

/** The pencil on the quiet-hours card — two times and the days that are off. */
function QuietHoursModal({
  open, quiet, onClose, onSave,
}: {
  open: boolean;
  quiet: Quiet;
  onClose: () => void;
  onSave: (next: Quiet) => void;
}) {
  const [from, setFrom] = useState(quiet.from);
  const [to, setTo] = useState(quiet.to);
  const [days, setDays] = useState<number[]>(quiet.daysOff);

  useEffect(() => {
    if (open) {
      setFrom(quiet.from);
      setTo(quiet.to);
      setDays(quiet.daysOff);
    }
  }, [open, quiet]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Quiet hours"
      sub="when nothing leaves, whatever is switched on"
      width={520}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary"
            onClick={() => onSave({ from, to, daysOff: days })}>
            Use these hours
          </button>
        </>
      }
    >
      <div className="cm-fields" style={{ display: 'flex', gap: 12, paddingTop: 10 }}>
        <div style={{ flex: 1 }}>
          <label className="t-ovl mut" htmlFor="quiet-from">From</label>
          <input id="quiet-from" className="cm-inp" type="time" value={from}
            onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label className="t-ovl mut" htmlFor="quiet-to">Until</label>
          <input id="quiet-to" className="cm-inp" type="time" value={to}
            onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      <p className="t-ovl mut" style={{ marginTop: 14 }}>Days nothing leaves at all</p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
        {DAY_NAMES.map((name, i) => {
          const off = days.includes(i);
          return (
            <button key={name} type="button"
              className={`chip chip-sm ${off ? 'chip-brand w6' : 'chip-outline'}`}
              onClick={() => setDays((d) => (off ? d.filter((x) => x !== i) : [...d, i].sort()))}>
              {name.slice(0, 3)}
            </button>
          );
        })}
      </div>
      <p className="t-cap mut mt8">
        The window is read in this clinic&rsquo;s own clock, and it may cross midnight — 9 pm to
        8 am is the usual shape.
      </p>
    </Modal>
  );
}
